import { useEffect, useState, useCallback } from "react";
import ClientLockService from "../lib/ClientLockService";
import { useAuth } from "../lib/AuthContext";

interface ClientLock {
  clientId: string;
  userId: string;
  userName: string;
  lockedAt: string;
  expiresAt: string;
}

interface UseClientLockReturn {
  isClientLocked: (clientId: string) => boolean;
  isClientLockedByCurrentUser: (clientId: string) => Promise<boolean>;
  getClientLock: (clientId: string) => ClientLock | null;
  lockClient: (clientId: string) => Promise<boolean>;
  unlockClient: (clientId: string) => Promise<void>;
  isLocking: boolean;
  lockError: string | null;
  lockService: ClientLockService | null;
}

export const useClientLock = (): UseClientLockReturn => {
  const [isLocking, setIsLocking] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const [lockService, setLockService] = useState<ClientLockService | null>(
    null
  );
  const [lockStateVersion, setLockStateVersion] = useState(0);
  const auth = useAuth();

  // Check if client locking feature is enabled via database feature flag
  const isClientLockingEnabled =
    auth?.featureFlags?.includes("client_lock") || false;

  // Initialize the lock service when auth is ready and feature is enabled
  useEffect(() => {
    if (!isClientLockingEnabled) {
      return;
    }

    if (auth?.isAuthenticated && auth?.currentUser?.agency_id) {
      const service = ClientLockService.getInstance();

      // Check if service is already initialized
      if (service.isServiceInitialized()) {
        console.log("Client lock service already initialized");
        setLockService(service);
        return;
      }

      // Initialize service asynchronously (non-blocking)
      service
        .initialize()
        .then(() => {
          setLockService(service);
          console.log("Client lock service initialized successfully");
        })
        .catch((error) => {
          console.error("Failed to initialize client lock service:", error);
          setLockError("Failed to initialize client locking");
          // Set service anyway - allow page to load
          setLockService(service);
        });
    }

    return () => {
      // Don't destroy the service on unmount as it's a singleton
      // The service will be destroyed when the page unloads
    };
  }, [
    auth?.isAuthenticated,
    auth?.currentUser?.agency_id,
    isClientLockingEnabled,
  ]);

  // Subscribe to real-time lock state changes from Pusher
  useEffect(() => {
    if (!lockService || !isClientLockingEnabled) return;

    // Subscribe to lock state changes for instant UI updates
    const unsubscribe = lockService.onLockStateChange(() => {
      console.log("🔔 Lock state changed - triggering re-render");
      setLockStateVersion((prev) => prev + 1);
    });

    return () => {
      unsubscribe();
    };
  }, [lockService, isClientLockingEnabled]);

  const isClientLocked = useCallback(
    (clientId: string): boolean => {
      if (!isClientLockingEnabled || !lockService) return false;
      // Force re-render by including lockStateVersion
      return lockService.isClientLocked(clientId);
    },
    [lockService, isClientLockingEnabled, lockStateVersion]
  );

  const isClientLockedByCurrentUser = useCallback(
    async (clientId: string): Promise<boolean> => {
      if (!isClientLockingEnabled || !lockService) return false;
      return lockService.isClientLockedByCurrentUser(clientId);
    },
    [lockService, isClientLockingEnabled, lockStateVersion]
  );

  const getClientLock = useCallback(
    (clientId: string): ClientLock | null => {
      if (!isClientLockingEnabled || !lockService) return null;
      return lockService.getClientLock(clientId);
    },
    [lockService, isClientLockingEnabled, lockStateVersion]
  );

  const lockClient = useCallback(
    async (clientId: string): Promise<boolean> => {
      if (!isClientLockingEnabled) {
        return true; // Allow editing when feature is disabled
      }

      setIsLocking(true);
      setLockError(null);

      try {
        // Use singleton directly if state variable isn't set yet
        const service = lockService || ClientLockService.getInstance();

        // If service isn't initialized, wait briefly for it (max 2 seconds)
        if (!service.isServiceInitialized()) {
          console.log("⏳ Service initializing, waiting up to 2 seconds...");
          const startTime = Date.now();
          const maxWait = 2000; // 2 seconds max

          // Start initialization if not already started
          const initPromise = service.initialize().catch(() => {});

          // Wait for either initialization to complete or timeout
          const timeoutPromise = new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              if (
                service.isServiceInitialized() ||
                Date.now() - startTime > maxWait
              ) {
                clearInterval(checkInterval);
                resolve();
              }
            }, 50); // Check every 50ms
          });

          await Promise.race([initPromise, timeoutPromise]);

          if (!service.isServiceInitialized()) {
            console.warn(
              "⚠️ Service init timeout - allowing edit without lock"
            );
            return true; // Timeout - allow editing anyway
          }

          console.log("✅ Service ready, proceeding with lock");
        }

        console.log(`🔒 Attempting to lock client ${clientId}...`);
        const success = await service.lockClient(clientId);

        if (success) {
          console.log(`✅ Lock acquired for client ${clientId}`);
          // Trigger state update to force re-render
          setLockStateVersion((prev) => prev + 1);
        } else {
          console.log(`❌ Lock failed for client ${clientId} - already locked`);
          setLockError("Client is already being edited by another user");
        }
        return success;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to lock client";
        console.error(`❌ Lock error for client ${clientId}:`, error);
        setLockError(errorMessage);
        // Don't block navigation on error
        return true;
      } finally {
        setIsLocking(false);
      }
    },
    [lockService, isClientLockingEnabled]
  );

  const unlockClient = useCallback(
    async (clientId: string): Promise<void> => {
      if (!isClientLockingEnabled || !lockService) return;

      try {
        await lockService.unlockClient(clientId);
        // Trigger state update to force re-render
        setLockStateVersion((prev) => prev + 1);
      } catch (error) {
        console.error("Failed to unlock client:", error);
      }
    },
    [lockService, isClientLockingEnabled]
  );

  return {
    isClientLocked,
    isClientLockedByCurrentUser,
    getClientLock,
    lockClient,
    unlockClient,
    isLocking,
    lockError,
    lockService, // Expose lockService for subscriptions
  };
};

// Hook for managing locks for a specific client
export const useClientLockForClient = (clientId: string) => {
  const {
    isClientLocked,
    isClientLockedByCurrentUser,
    getClientLock,
    lockClient,
    unlockClient,
    isLocking,
    lockError,
    lockService,
  } = useClientLock();

  const [isLocked, setIsLocked] = useState(false);
  const [isLockedByMe, setIsLockedByMe] = useState(false);
  const [lockInfo, setLockInfo] = useState<ClientLock | null>(null);
  const [, forceUpdate] = useState({});

  // Update lock status when clientId changes or lock state changes
  useEffect(() => {
    if (!clientId) return;

    const updateLockStatus = async () => {
      const locked = isClientLocked(clientId);
      const lockedByMe = await isClientLockedByCurrentUser(clientId);
      const lock = getClientLock(clientId);

      console.log(`🔄 Lock status updated for client ${clientId}:`, {
        locked,
        lockedByMe,
      });

      setIsLocked(locked);
      setIsLockedByMe(lockedByMe);
      setLockInfo(lock);
    };

    updateLockStatus();

    // Subscribe to real-time lock state changes
    let unsubscribe: (() => void) | undefined;
    if (lockService) {
      unsubscribe = lockService.onLockStateChange(() => {
        console.log(`🔔 Lock state changed for client ${clientId}`);
        updateLockStatus();
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [
    clientId,
    isClientLocked,
    isClientLockedByCurrentUser,
    getClientLock,
    lockService,
  ]);

  const handleLockClient = useCallback(async (): Promise<boolean> => {
    if (!clientId) {
      return false;
    }

    try {
      const success = await lockClient(clientId);

      if (success) {
        // Force immediate update of lock status
        const locked = isClientLocked(clientId);
        const lockedByMe = await isClientLockedByCurrentUser(clientId);
        const lock = getClientLock(clientId);

        setIsLocked(locked);
        setIsLockedByMe(lockedByMe);
        setLockInfo(lock);
      }
      return success;
    } catch (error) {
      console.error("Error in handleLockClient:", error);
      return false;
    }
  }, [
    clientId,
    lockClient,
    isClientLocked,
    isClientLockedByCurrentUser,
    getClientLock,
  ]);

  const handleUnlockClient = useCallback(async (): Promise<void> => {
    if (!clientId) {
      return;
    }

    try {
      await unlockClient(clientId);

      // Force immediate update of lock status
      const locked = isClientLocked(clientId);
      const lockedByMe = await isClientLockedByCurrentUser(clientId);
      const lock = getClientLock(clientId);

      setIsLocked(locked);
      setIsLockedByMe(lockedByMe);
      setLockInfo(lock);
    } catch (error) {
      console.error("Error in handleUnlockClient:", error);
    }
  }, [
    clientId,
    unlockClient,
    isClientLocked,
    isClientLockedByCurrentUser,
    getClientLock,
  ]);

  return {
    isLocked,
    isLockedByMe,
    lockInfo,
    lockClient: handleLockClient,
    unlockClient: handleUnlockClient,
    isLocking,
    lockError,
  };
};
